import { useEffect, useMemo, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { selectOrderState, selectSelectedItemIds, setItemSelection } from '@/features/order/orderSlice';
import { buildCanonicalOrder } from '@/features/order/utils/canonicalOrder';
import type { Order, SupervisorEvent } from '@/types/supervisor';
import type { PrintableOrderItem } from '../ItemStepPanelLayout';
import { filterPrintableItems } from '../ItemStepPanelLayout';

interface UsePrintableItemTargetsResult {
  readonly printableItems: readonly PrintableOrderItem[];
  readonly targetedItems: readonly PrintableOrderItem[];
  readonly hasSelectedItems: boolean;
  readonly selectionSet: ReadonlySet<string>;
}

export const usePrintableItemTargets = (order: Order): UsePrintableItemTargetsResult => {
  const dispatch = useAppDispatch();
  const selectedItemIds = useAppSelector(selectSelectedItemIds);
  const orderState = useAppSelector(selectOrderState);

  const printableItems = useMemo(() => {
    const canonicalOrder = buildCanonicalOrder(orderState, order);
    return filterPrintableItems(canonicalOrder);
  }, [order, orderState]);
  const selectionSet = useMemo<ReadonlySet<string>>(() => new Set(selectedItemIds), [selectedItemIds]);
  const autoSelectionRegistryRef = useRef<Map<string, { autoApplied: boolean; hasBeenSelected: boolean }>>(new Map());

  useEffect(() => {
    const registry = autoSelectionRegistryRef.current;

    const currentIds = new Set(
      printableItems
        .map((item) => (typeof item?.id === 'string' && item.id.length > 0 ? item.id : null))
        .filter((id): id is string => id != null),
    );

    for (const key of Array.from(registry.keys())) {
      if (!currentIds.has(key)) {
        registry.delete(key);
      }
    }

    for (const id of currentIds) {
      const existing = registry.get(id);
      const hasBeenSelected = selectionSet.has(id) || existing?.hasBeenSelected === true;
      if (!existing) {
        registry.set(id, { autoApplied: false, hasBeenSelected });
      } else if (existing.hasBeenSelected !== hasBeenSelected) {
        registry.set(id, { ...existing, hasBeenSelected });
      }
    }

    if (printableItems.length !== 1) {
      return;
    }

    const identifier = printableItems[0]?.id;
    if (typeof identifier !== 'string' || identifier.length === 0) {
      return;
    }

    const existing = registry.get(identifier) ?? { autoApplied: false, hasBeenSelected: false };
    const hasBeenSelected = existing.hasBeenSelected || selectionSet.has(identifier);

    if (!selectionSet.has(identifier) && !existing.autoApplied && !hasBeenSelected) {
      dispatch(setItemSelection({ itemId: identifier, selected: true }));
      registry.set(identifier, { autoApplied: true, hasBeenSelected: true });
      return;
    }

    registry.set(identifier, { autoApplied: existing.autoApplied, hasBeenSelected });
  }, [dispatch, printableItems, selectionSet]);

  const hasSelectedItems = useMemo(() => {
    return printableItems.some(item => selectionSet.has(item.id));
  }, [printableItems, selectionSet]);

  const targetedItems = useMemo(() => {
    if (!hasSelectedItems) {
      return printableItems.length === 1 ? printableItems : [];
    }
    return printableItems.filter(item => selectionSet.has(item.id));
  }, [hasSelectedItems, printableItems, selectionSet]);

  return {
    printableItems,
    targetedItems,
    hasSelectedItems,
    selectionSet,
  };
};

interface UseSelectionGuardOptions {
  readonly printableItems: readonly PrintableOrderItem[];
  readonly hasSelectedItems: boolean;
  readonly send: (event: SupervisorEvent) => void;
  readonly fallbackMessage: string;
  readonly aiPrompt: string;
}

export const useSelectionGuard = ({
  printableItems,
  hasSelectedItems,
  send,
  fallbackMessage,
  aiPrompt,
}: UseSelectionGuardOptions) => {
  const notifiedRef = useRef(false);

  useEffect(() => {
    if (printableItems.length > 1 && !hasSelectedItems) {
      if (!notifiedRef.current) {
        send({
          type: 'UI.REQUEST_AI_PROMPT',
          aiPrompt,
          fallbackMessage,
        });
        notifiedRef.current = true;
      }
      send({ type: 'UI.GOTO_STEP', step: 'uploadedImages' });
    } else if (hasSelectedItems || printableItems.length <= 1) {
      notifiedRef.current = false;
    }
  }, [aiPrompt, fallbackMessage, hasSelectedItems, printableItems.length, send]);
};
